import { IsVarEmpty, isStringNotEmpty } from '../../NonReact/utils/GlobalFunctions'
import {
    ADMIN_URL_QUERY_CME,
    ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN,
    ADMIN_URL_QUERY_PARTITION,
    ADMIN_URL_QUERY_PARTITION_GUID,
    ADMIN_URL_QUERY_TELEPHONY_SERVER
} from './adminListUrlParams'
import { ALL_VALUE, ANY_VALUE_STRING_NUMERIC } from '../../React/Common/Constants/DefaultValues'
import { DEFAULT_CME_ID } from '../../React/Common/Constants/ServerValues'
import type { AdminFilterValues } from '../Components/Molecules/AdminFilters/AdminFilters'
import { EMPTY_STRING } from '../../React/Common/Constants/Separators'
import {
    LEGACY_ADMIN_ENTITY_LIST_CM_ENVIRONMENT_PARAM,
    LEGACY_ADMIN_ENTITY_LIST_PARTITION_PARAM,
    LEGACY_ADMIN_ENTITY_LIST_RECURSIVE_CME_TELSYS_PARAM,
    LEGACY_ADMIN_ENTITY_LIST_TELSYS_PARAM
} from './adminLegacyServletUrls'

/**
 * localStorage key: when set, each load of the matching v2 admin list route redirects to the classic servlet list;
 * the entry is kept until overwritten (same hotkey) or cleared explicitly.
 */
export const LEGACY_ADMIN_LIST_REDIRECT_STORAGE_KEY = 'akixi_admin_list_legacy_redirect_v1'

/**
 * Payload persisted before {@code location.reload()} so the post-reload boot pass can open the legacy servlet.
 */
export type LegacyAdminListRedirectPayload = {
    readonly version: 1
    readonly servletCommand: string
    readonly legacyQuery: Record<string, string>
}

/**
 * True when the filter value is a concrete TelSys or partition selection (excludes [All] / Any placeholders).
 *
 * @param value - Raw telephony or partition option value from admin filters.
 * @returns Whether the value should be sent in legacy list query parameters.
 */
export function isConcreteTelSysOrPartitionFilterValueForLegacy(value: unknown): boolean {
    if (IsVarEmpty(value)) return false

    if (value === ALL_VALUE) return false

    if (value === ANY_VALUE_STRING_NUMERIC) return false

    return true
}

/**
 * Builds legacy servlet query parameters for admin entity lists from the v2 admin filter bar state.
 *
 * @param filters - Current admin filter values (may be undefined before hydration).
 * @param userScopeCmeId - Signed-in user's default CM Environment when no CME is selected in the bar.
 * @returns Key/value pairs for {@link redirectToServletWithBranding} (cbCMEnvironmentID, cbTelSysID, etc.).
 */
export function buildLegacyAdminEntityListQueryFromFilters(
    filters: Definable<AdminFilterValues>,
    userScopeCmeId: Definable<string>
): Record<string, string> {
    const cmeId: string = String(filters?.cme?.value ?? userScopeCmeId ?? DEFAULT_CME_ID)
    const telVal: unknown = filters?.telephonyServer?.value
    const partVal: unknown = filters?.partition?.value
    const includeRecursive: boolean = filters?.includeCmeChildren ?? true
    const telSysId: string = isConcreteTelSysOrPartitionFilterValueForLegacy(telVal)
        ? String(telVal)
        : String(ALL_VALUE)
    const out: Record<string, string> = {
        [LEGACY_ADMIN_ENTITY_LIST_CM_ENVIRONMENT_PARAM]: cmeId,
        [LEGACY_ADMIN_ENTITY_LIST_TELSYS_PARAM]: telSysId,
        [LEGACY_ADMIN_ENTITY_LIST_RECURSIVE_CME_TELSYS_PARAM]: includeRecursive ? 'true' : 'false'
    }

    if (
        isConcreteTelSysOrPartitionFilterValueForLegacy(telVal) &&
        isConcreteTelSysOrPartitionFilterValueForLegacy(partVal)
    ) {
        out[LEGACY_ADMIN_ENTITY_LIST_PARTITION_PARAM] = String(partVal)
    }

    return out
}

/**
 * Builds legacy servlet query parameters for admin entity lists from a v2 admin list route's own query string.
 *
 * Drill-through links between admin lists carry their filter as v2 query parameters (for example
 * {@code /device?cme=…&telephonyServer=14}). When the destination list page is still behind a feature flag its
 * feature gate redirects to the classic servlet, so without this mapping the filter would be dropped and the user
 * would land on an unfiltered legacy list (APD-9657).
 *
 * @param search - The route's raw query string, normally {@code window.location.search}.
 * @returns Key/value pairs for {@link redirectToServlet} (cbCMEnvironmentID, cbTelSysID, etc.); empty when the
 *          route carried no recognised filter parameters.
 */
export function buildLegacyAdminEntityListQueryFromUrl(search: Definable<string>): Record<string, string> {
    const out: Record<string, string> = {}
    if (!isStringNotEmpty(search)) return out

    const params: URLSearchParams = new URLSearchParams(search)

    const cmeId: OnlyNullable<string> = params.get(ADMIN_URL_QUERY_CME)
    if (isStringNotEmpty(cmeId)) out[LEGACY_ADMIN_ENTITY_LIST_CM_ENVIRONMENT_PARAM] = cmeId

    const telSysId: OnlyNullable<string> = params.get(ADMIN_URL_QUERY_TELEPHONY_SERVER)
    if (isConcreteTelSysOrPartitionFilterValueForLegacy(telSysId)) {
        out[LEGACY_ADMIN_ENTITY_LIST_TELSYS_PARAM] = String(telSysId)

        // The legacy list only honours a partition filter alongside a concrete telephony server, mirroring
        // buildLegacyAdminEntityListQueryFromFilters above. Its partition control is keyed by GUID, so prefer the
        // GUID a drill-through link carries and only fall back to the ID within the telephony server.
        const partitionGuid: OnlyNullable<string> = params.get(ADMIN_URL_QUERY_PARTITION_GUID)
        const partitionId: OnlyNullable<string> = params.get(ADMIN_URL_QUERY_PARTITION)
        if (isStringNotEmpty(partitionGuid)) {
            out[LEGACY_ADMIN_ENTITY_LIST_PARTITION_PARAM] = partitionGuid
        } else if (isConcreteTelSysOrPartitionFilterValueForLegacy(partitionId)) {
            out[LEGACY_ADMIN_ENTITY_LIST_PARTITION_PARAM] = String(partitionId)
        }
    }

    const includeCmeChildren: OnlyNullable<string> = params.get(ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN)
    if (isStringNotEmpty(includeCmeChildren)) {
        out[LEGACY_ADMIN_ENTITY_LIST_RECURSIVE_CME_TELSYS_PARAM] = includeCmeChildren === 'true' ? 'true' : 'false'
    }

    return out
}

/**
 * Reads the pending legacy redirect payload without mutating storage.
 *
 * @returns Parsed payload, or null when absent or invalid.
 */
export function readLegacyAdminListRedirectPayload(): OnlyNullable<LegacyAdminListRedirectPayload> {
    if (typeof window === 'undefined') return null

    try {
        const raw: OnlyNullable<string> = window.localStorage.getItem(LEGACY_ADMIN_LIST_REDIRECT_STORAGE_KEY)
        if (IsVarEmpty(raw) || raw === EMPTY_STRING) return null

        const parsed: unknown = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') return null

        const o = parsed as Record<string, unknown>
        if (o.version !== 1) return null

        if (typeof o.servletCommand !== 'string' || o.servletCommand === EMPTY_STRING) return null

        if (!o.legacyQuery || typeof o.legacyQuery !== 'object') return null

        const legacyQuery = o.legacyQuery as Record<string, unknown>
        const entries: Record<string, string> = Object.fromEntries(
            Object.entries(legacyQuery).flatMap(([k, v]): [string, string][] => (typeof v === 'string' ? [[k, v]] : []))
        )

        return { version: 1, servletCommand: o.servletCommand, legacyQuery: entries }
    } catch {
        return null
    }
}

/**
 * Clears the legacy redirect payload from localStorage (optional; redirect no longer clears storage automatically).
 */
export function clearLegacyAdminListRedirectPayload(): void {
    if (typeof window === 'undefined') return

    window.localStorage.removeItem(LEGACY_ADMIN_LIST_REDIRECT_STORAGE_KEY)
}

/**
 * Persists the payload read on each matching v2 list mount; not removed automatically when the servlet redirect runs.
 *
 * @param payload - Servlet command and legacy query keys to apply on redirect.
 */
export function storeLegacyAdminListRedirectPayload(payload: LegacyAdminListRedirectPayload): void {
    window.localStorage.setItem(LEGACY_ADMIN_LIST_REDIRECT_STORAGE_KEY, JSON.stringify(payload))
}
